import { DATE_FORMAT, HighlightIcon, Table } from '@dev-spendesk/grapes';
import { useState } from 'react';

import { useCompany } from 'modules/app/hooks/useCompany';
import { type MileageScheme } from 'modules/requests/models/mileageScheme';
import type { RequestAPI } from 'modules/requests/types';
import { UserAvatar } from 'modules/users/components';
import { EllipsisTooltip } from 'src/core/common/components/EllipsisTooltip';
import { PageNoResult } from 'src/core/common/components/PageNoResult';
import { SupplierLogo } from 'src/core/common/components/SupplierLogo';
import { useParams } from 'src/core/common/hooks/useParams';
import { useTranslation } from 'src/core/common/hooks/useTranslation';
import { routes } from 'src/core/constants/routes';
import { useUser } from 'src/core/modules/app/hooks/useUser';
import { RequestStateTag } from 'src/core/modules/requests/list/components/RequestStateTag/RequestStateTag';
import { RequestsTableActions } from 'src/core/modules/requests/list/components/RequestsListBox/RequestsTableActions';
import { RequestsListLoader } from 'src/core/modules/requests/list/components/RequestsListsLoader';
import {
  getRequestDistanceFormatted,
  hasOverriddenDistance,
} from 'src/core/modules/requests/models/mileageRequest';
import {
  canCancelRequest,
  isRequestApprovable,
} from 'src/core/modules/requests/models/request';
import { SubnavigationItem } from 'src/core/modules/requests/utils/navigation';
import { getAmountToDisplay } from 'src/core/utils/entities/request';
import { formatMonetaryValue } from 'src/core/utils/monetaryValue';
import { displayMoney, formatMoney } from 'src/core/utils/money';
import { logger } from 'src/utils/datadog-log-wrapper';

import { getSection, sectionGroupByRequestType } from './utils';
import type { RequestStats, Sections } from '../requestsSectionsProps';

type Request = RequestAPI & {
  mileage_scheme: MileageScheme;
};

type Props = {
  requests: Request[];
  sectionsStats: Sections<RequestStats>;
  onOptionClick: (request: Request) => void;
  type: SubnavigationItem;
  hasFiltersApplied?: boolean;
  isLoading?: boolean;
};

export const RequestsTable = ({
  requests,
  onOptionClick,
  sectionsStats,
  type,
  isLoading,
  hasFiltersApplied,
}: Props) => {
  const { t, localeFormat } = useTranslation('global');
  const { id: pathRequestId } = useParams(routes.REQUESTS.path);

  const user = useUser();
  const company = useCompany();

  const [selectedRowIds, setSelectedRowsIds] = useState<string[]>([]);

  if (isLoading) {
    return <RequestsListLoader />;
  }

  if (requests.length === 0) {
    return (
      <PageNoResult
        mode={type === SubnavigationItem.Mine ? 'mineRequest' : 'request'}
        hasFiltersApplied={hasFiltersApplied}
      />
    );
  }

  return (
    <>
      <Table
        className="my-24"
        columns={[
          ...(type !== 'mine'
            ? [
                {
                  id: 'user',
                  width: '272px',
                  renderCell: (request: Request) => {
                    return (
                      <div className="max-w-full flex items-center gap-8">
                        <UserAvatar
                          id={request.user_id ?? ''}
                          name={
                            request.user?.fullname ?? request.user?.email ?? ''
                          }
                          avatar={request.user?.avatar ?? ''}
                        />
                        <div className="flex flex-col truncate body-m">
                          <span className="text-content-primary">
                            <EllipsisTooltip
                              text={
                                request.user?.fullname ??
                                request.user?.email ??
                                ''
                              }
                              data-mask
                            />
                          </span>
                          {request.cost_center && (
                            <span className="text-content-secondary-bg-secondary">
                              <EllipsisTooltip
                                text={request.cost_center?.name}
                              />
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  },
                  header: t('requests.employeeHeaderLabel'),
                },
              ]
            : []),
          {
            id: 'amount',
            width: '144px',
            renderCell: (request) => {
              return formatMoney(
                getAmountToDisplay(request),
                request.currency_declared,
              );
            },
            header: t('requests.toApproveList.amount'),
          },
          {
            id: 'description',
            renderCell: (request) => {
              if (request.type === 'mileage_allowance') {
                return (
                  <div className="flex items-center gap-4 truncate">
                    <EllipsisTooltip
                      text={`${getRequestDistanceFormatted({
                        mileageRequest: request,
                      })}${
                        hasOverriddenDistance(request)
                          ? ` ${t('requests.panel.mileage.manualInput')}`
                          : ''
                      } • ${request.description}`}
                    />
                  </div>
                );
              }
              return (
                <div className="truncate">
                  <EllipsisTooltip text={request.description} />
                </div>
              );
            },
            header: t('requests.toApproveList.description'),
          },
          {
            id: 'supplier',
            renderCell: (request) => {
              if (request.type === 'mileage_allowance') {
                return (
                  <div className="flex items-center gap-8 truncate">
                    <HighlightIcon
                      name="car"
                      size={32}
                      variant="lemon"
                      className="shrink-0"
                    />
                    <EllipsisTooltip
                      text={t('forms.paymentType.mileageAllowance')}
                    />
                  </div>
                );
              }
              if (request.type === 'per_diem_allowance') {
                return (
                  <div className="flex items-center gap-8 truncate">
                    <HighlightIcon
                      className="shrink-0"
                      name="globe-eu-africa"
                      size={32}
                      variant="lemon"
                    />
                    <EllipsisTooltip
                      text={t('forms.paymentType.perDiemAllowance')}
                    />
                  </div>
                );
              }
              if (request.type === 'travel_budget') {
                return (
                  <div className="flex items-center gap-8 truncate">
                    <HighlightIcon
                      className="shrink-0"
                      name="plane"
                      size={32}
                      variant="pink"
                    />
                    <EllipsisTooltip
                      text={t('payables.panel.types.travelBudget')}
                    />
                  </div>
                );
              }
              return (
                <div className="flex gap-8">
                  {request?.supplier?.name ? (
                    <div className="flex items-center gap-8 truncate">
                      <SupplierLogo name={request.supplier.name} size={32} />
                      <EllipsisTooltip text={request.supplier.name} />
                    </div>
                  ) : (
                    t('misc.na')
                  )}
                </div>
              );
            },
            header: t('requests.toApproveList.supplier'),
          },
          {
            id: 'status',
            width: '192px',
            renderCell: (request) => (
              <div className="text-wrap">
                <RequestStateTag request={request} />
              </div>
            ),
            header: t('misc.status'),
          },
          {
            id: 'date',
            width: '144px',
            renderCell: (request) =>
              localeFormat(new Date(request.created_at), DATE_FORMAT.MEDIUM),
            header: t('requests.toApproveList.date'),
          },
        ]}
        data={requests}
        groupBy={(request) => sectionGroupByRequestType[request.type]}
        getIsRowActive={(request) => request.id === pathRequestId}
        getRowId={(request) => request.id}
        selectedRowIds={selectedRowIds}
        onRowClick={onOptionClick}
        onRowSelectionChange={(_, id, checked) => {
          setSelectedRowsIds((options) => {
            if (checked) {
              return options.concat(id);
            }
            return options.filter((optionId) => optionId !== id);
          });
        }}
        onAllRowsSelectionChange={(_, ids, checked) => {
          setSelectedRowsIds(checked ? ids : []);
        }}
        renderGroupedRowHeader={(value, aggregatedRows) => {
          const section = getSection(type, user)[value];
          if (!section) {
            return null;
          }
          const translationKey = section.translationKey;

          const sectionStats = sectionsStats[section.sectionStatsKey];
          const useAggregatedRows = value === 'expense';
          return (
            <div className="flex items-center py-8 uppercase text-content-secondary-bg-secondary body-s">
              <span className="">{t(translationKey)}</span>
              <div className="ml-auto flex items-center gap-8">
                <span>
                  {useAggregatedRows
                    ? t('misc.requestWithCount', {
                        count: aggregatedRows.length,
                      })
                    : t('misc.requestWithCount', {
                        count: sectionStats?.count,
                      })}
                </span>
                {useAggregatedRows && (
                  <>
                    <span
                      className="h-4 w-4 rounded-[50%]"
                      style={{ background: 'var(--grapes-color-border-border-default)' }}
                    />
                    <span>
                      {displayMoney({
                        // This method might only be valid for expense claim requests
                        value: aggregatedRows.reduce(
                          (accumulator, row) => {
                            const amountToSum =
                              row.amount_to_refund ??
                              row.original_converted_amount;
                            if (
                              amountToSum === undefined ||
                              amountToSum === null
                            ) {
                              logger.error(
                                'Invalid amount to sum for expense',
                                {
                                  team: 'travel-and-expenses',
                                  scope: 'requests::table',
                                  requestId: row.id,
                                },
                              );
                              return accumulator;
                            }
                            return amountToSum + accumulator;
                          },

                          0,
                        ),
                        currency: company.currency,
                      })}
                    </span>
                  </>
                )}
                {sectionStats?.totalAmount && !useAggregatedRows && (
                  <>
                    <span
                      className="h-4 w-4 rounded-[50%]"
                      style={{ background: 'var(--grapes-color-border-border-default)' }}
                    />
                    <span>{formatMonetaryValue(sectionStats.totalAmount)}</span>
                  </>
                )}
              </div>
            </div>
          );
        }}
      />
      <RequestsTableActions
        selectedRowIds={selectedRowIds}
        type={type}
        unselectRequests={() => setSelectedRowsIds([])}
        requests={requests}
        isItemEligibleForAction={(request, action: string) => {
          if (!request) {
            return false;
          }

          switch (action) {
            case 'approve':
            case 'deny':
              return isRequestApprovable(request);
            case 'cancel':
              return canCancelRequest(request, user);
            case 'download':
              return true;
            default:
              return false;
          }
        }}
      />
    </>
  );
};
